import { useState, useEffect } from 'react';
import { authApi } from '../../services/api';
import { formatDateTime } from '../../utils/formatters';
import { useAuth } from '../../context/AuthContext';
import AddUserModal from './AddUserModal';
import LoadingSpinner from '../common/LoadingSpinner';

const ROLE_BADGES = {
  admin: 'badge-blue',
  developer: 'badge-yellow',
  management: 'badge-green',
};

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null); // userId being edited

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await authApi.listUsers();
      setUsers(res.data.users);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (formData) => {
    await authApi.register(formData);
    await loadUsers();
  };

  const handleToggleStatus = async (userId) => {
    try {
      await authApi.toggleUserStatus(userId);
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await authApi.updateUser(userId, { role: newRole });
      setEditingRole(null);
      await loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update role');
    }
  };

  if (loading) return <LoadingSpinner message="Loading users..." />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-100">User Management</h1>
          <p className="text-sm text-gray-500 mt-1">{users.length} registered users</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn-primary flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add User
        </button>
      </div>

      {/* User Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-600">
                <th className="table-header text-left py-3 px-3">Name</th>
                <th className="table-header text-left py-3">Email</th>
                <th className="table-header text-center py-3">Role</th>
                <th className="table-header text-center py-3">Status</th>
                <th className="table-header text-left py-3">Last Login</th>
                <th className="table-header text-left py-3">Created</th>
                <th className="table-header text-right py-3 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id} className="border-b border-surface-700 hover:bg-surface-700/50">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-surface-600 flex items-center justify-center text-xs font-bold text-gray-300">
                          {(u.full_name || '?')[0].toUpperCase()}
                        </div>
                        <span className="text-gray-200 font-medium">{u.full_name}</span>
                        {isSelf && <span className="text-xs text-gray-600">(you)</span>}
                      </div>
                    </td>
                    <td className="py-3 text-gray-400">{u.email}</td>
                    <td className="py-3 text-center">
                      {editingRole === u.id ? (
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                          onBlur={() => setEditingRole(null)}
                          autoFocus
                          className="select-field text-xs py-1 px-2"
                        >
                          <option value="developer">developer</option>
                          <option value="management">management</option>
                          <option value="admin">admin</option>
                        </select>
                      ) : (
                        <span
                          className={`${ROLE_BADGES[u.role] || 'badge-blue'} ${!isSelf ? 'cursor-pointer' : ''}`}
                          onClick={() => !isSelf && setEditingRole(u.id)}
                          title={!isSelf ? 'Click to change role' : undefined}
                        >
                          {u.role}
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${u.is_active ? 'text-emerald-400' : 'text-red-400'}`}>
                        <span className={`w-2 h-2 rounded-full ${u.is_active ? 'bg-emerald-400' : 'bg-red-400'}`} />
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 text-gray-500 text-xs">{u.last_login_at ? formatDateTime(u.last_login_at) : 'Never'}</td>
                    <td className="py-3 text-gray-500 text-xs">{formatDateTime(u.created_at)}</td>
                    <td className="py-3 text-right px-3">
                      {!isSelf && (
                        <button
                          onClick={() => handleToggleStatus(u.id)}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            u.is_active
                              ? 'text-red-400 hover:bg-red-500/10'
                              : 'text-emerald-400 hover:bg-emerald-500/10'
                          }`}
                        >
                          {u.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <AddUserModal onClose={() => setShowAddModal(false)} onSubmit={handleAddUser} />
      )}
    </div>
  );
}
